import * as THREE from "three";
import RAPIER from "@dimforge/rapier3d-compat";

import { HangingRig } from "../../../entities/hanging_rig.js";
import { HangConfig, PhysicsTable } from "../../../entities/physics_table.js";

const AXIS = new THREE.Vector3( 0, 0, 1 );
const TMP_Q_DELTA = new THREE.Quaternion();
const TMP_Q_BODY = new THREE.Quaternion();
const TMP_V = new THREE.Vector3();

export class TableSwinger
{
  private time = 0;
  private angle = 0;

  constructor(
    private readonly rig: HangingRig,
    private readonly config: HangConfig,
    private readonly get_bodies: ( table: PhysicsTable ) => Iterable<RAPIER.RigidBody>
  )
  {}

  public step( dt: number ): void
  {
    this.time += dt;

    const period = Math.max( this.config.swingPeriodSec, 0.1 );
    const swing_rad = this.config.swingDeg * Math.PI / 180;
    const next_angle = swing_rad * Math.sin( this.time * ( 2 * Math.PI / period ) );

    const delta = next_angle - this.angle;
    this.angle = next_angle;

    if ( delta !== 0 )
      this.applyDelta( delta );

    this.rig.setAngle( this.angle );
  }

  private applyDelta( delta: number ): void
  {
    const pivot = this.rig.getPivotWorld();

    TMP_Q_DELTA.setFromAxisAngle( AXIS, -delta );

    for ( const table of this.rig.getTables() )
      for ( const body of this.get_bodies( table ) )
      {
        if ( !body.isValid() )
          continue;

        const p = body.translation();

        if ( table.isOutside( p ) )
          continue;

        TMP_V.set( p.x - pivot.x, p.y - pivot.y, p.z - pivot.z ).applyQuaternion( TMP_Q_DELTA );
        body.setTranslation(
          { x: pivot.x + TMP_V.x, y: pivot.y + TMP_V.y, z: pivot.z + TMP_V.z },
          false
        );

        const r = body.rotation();
        TMP_Q_BODY.set( r.x, r.y, r.z, r.w ).premultiply( TMP_Q_DELTA );
        body.setRotation(
          { x: TMP_Q_BODY.x, y: TMP_Q_BODY.y, z: TMP_Q_BODY.z, w: TMP_Q_BODY.w },
          false
        );

        if ( !body.isSleeping() )
        {
          const lv = body.linvel();
          TMP_V.set( lv.x, lv.y, lv.z ).applyQuaternion( TMP_Q_DELTA );
          body.setLinvel( { x: TMP_V.x, y: TMP_V.y, z: TMP_V.z }, false );

          const av = body.angvel();
          TMP_V.set( av.x, av.y, av.z ).applyQuaternion( TMP_Q_DELTA );
          body.setAngvel( { x: TMP_V.x, y: TMP_V.y, z: TMP_V.z }, false );
        }
      }
  }
}
